/**
 * Fachlogik des Talentpools (docs/talentpool-plan.md), rein: kein Supabase,
 * kein React, keine Uhr — „heute“ und der Suchpunkt kommen als Argument.
 *
 * Hier steht, was die Oberfläche anzeigt und wonach sie sortiert:
 * Beschriftungen der Wertelisten der Website, Filter, Entfernung zum
 * Suchpunkt („wer erreicht diese Baustelle?“), Löschfrist und die lesbaren
 * Zusatzangaben aus `angaben`. Die Komponenten unter
 * `src/components/talentpool/` bleiben dünn und rufen nur diese Funktionen.
 *
 * Die Luftlinie kommt aus `nearbyProjects.ts` und wird nicht erneut
 * ausgeschrieben (wie freelancerLogic, leadLogic).
 */
import { calculateDistanceKm } from '../nearbyProjects';
import {
    TALENT_GRENZEN,
    TALENT_STATUS,
    TALENT_STATUS_ABGESCHLOSSEN,
    type TalentArbeitsform,
    type TalentRolle,
    type TalentStatus,
    type TalentpoolZeile,
} from './talentpoolTypes';

// ─── Beschriftungen ──────────────────────────────────────────────────────────

/** Leerer Zustand der Liste, wörtlich vom Betreiber. */
export const TALENTPOOL_LEER_TEXT =
    'Noch keine Bewerbungen. Sobald jemand auf gefas-mbh.de eine Bewerbung absendet, erscheint sie hier.';

/** Rollen wie auf der Website (src/data/bewerbung.ts dort). */
export const ROLLE_LABEL: Readonly<Record<TalentRolle, string>> = {
    sigeko: 'SiGeKo',
    hse: 'HSE-Manager',
    sifa: 'Fachkraft für Arbeitssicherheit',
    mehrere: 'Mehreres',
};

/** Kurzform für Chips in der Liste — „Fachkraft für Arbeitssicherheit“ sprengt die Zeile. */
const ROLLE_KURZ: Readonly<Record<TalentRolle, string>> = {
    sigeko: 'SiGeKo',
    hse: 'HSE',
    sifa: 'SiFa',
    mehrere: 'Mehreres',
};

/** Nadelfarben je Rolle (Tailwind-600er-Stufen, auf OSM-Kacheln gut lesbar). */
export const ROLLE_FARBE: Readonly<Record<TalentRolle, string>> = {
    sigeko: '#2563eb', // blue-600
    hse: '#ea580c',    // orange-600
    sifa: '#059669',   // emerald-600
    mehrere: '#7c3aed', // violet-600
};
/** Unbekannte Rolle (die Route schreibt `unbekannt`, die Website kann neue Werte schicken). */
export const ROLLE_FARBE_SONST = '#64748b'; // slate-500

export const ARBEITSFORM_LABEL: Readonly<Record<TalentArbeitsform, string>> = {
    fest: 'Festanstellung',
    frei: 'Freie Mitarbeit',
    teilzeit: 'Teilzeit/Nebenjob',
};

export const STATUS_LABEL: Readonly<Record<TalentStatus, string>> = {
    neu: 'Neu',
    kontaktiert: 'Kontaktiert',
    gespraech: 'Gespräch',
    pool: 'Im Pool',
    zusage: 'Zusage',
    absage: 'Absage',
};

const hatSchluessel = <K extends string>(tabelle: Readonly<Record<K, string>>, x: string): x is K =>
    Object.prototype.hasOwnProperty.call(tabelle, x);

export const istTalentRolle = (x: string): x is TalentRolle => hatSchluessel(ROLLE_LABEL, x);

export const istTalentStatus = (x: unknown): x is TalentStatus =>
    typeof x === 'string' && (TALENT_STATUS as readonly string[]).includes(x);

const ohneRolle = (rolle: string): boolean => {
    const r = rolle.trim().toLowerCase();
    return r === '' || r === 'unbekannt';
};

/** Rolle für Legende und Detail; unbekannte Werte kommen als Text durch. */
export const rolleLabel = (rolle: string): string =>
    istTalentRolle(rolle) ? ROLLE_LABEL[rolle] : ohneRolle(rolle) ? 'Ohne Angabe' : rolle.trim();

export const rolleKurz = (rolle: string): string =>
    istTalentRolle(rolle) ? ROLLE_KURZ[rolle] : rolleLabel(rolle);

export const rolleFarbe = (rolle: string): string =>
    istTalentRolle(rolle) ? ROLLE_FARBE[rolle] : ROLLE_FARBE_SONST;

export const arbeitsformLabel = (form: string): string =>
    hatSchluessel(ARBEITSFORM_LABEL, form) ? ARBEITSFORM_LABEL[form] : form.trim();

export const statusLabel = (status: string): string =>
    istTalentStatus(status) ? STATUS_LABEL[status] : status;

export const istAbgeschlossen = (status: TalentStatus): boolean =>
    TALENT_STATUS_ABGESCHLOSSEN.includes(status);

/**
 * Legende der Karte: die vier Rollen der Website, dazu „Sonstige“, sobald
 * eine Bewerbung eine andere (oder keine) Rolle trägt.
 */
export const legendeRollen = (zeilen: readonly TalentpoolZeile[]): { label: string; farbe: string }[] => {
    const eintraege = (Object.keys(ROLLE_LABEL) as TalentRolle[]).map(r => ({ label: ROLLE_LABEL[r], farbe: ROLLE_FARBE[r] }));
    if (zeilen.some(z => !istTalentRolle(z.rolle))) eintraege.push({ label: 'Sonstige', farbe: ROLLE_FARBE_SONST });
    return eintraege;
};

// ─── Datum ───────────────────────────────────────────────────────────────────

const BERLIN = 'Europe/Berlin';
const NUR_DATUM = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Heutiges Datum in Berlin als YYYY-MM-DD — Bezug für die Löschfrist (eine `date`-Spalte). */
export const heuteIso = (jetzt: Date): string => {
    const teile = new Intl.DateTimeFormat('en-GB', { timeZone: BERLIN, year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(jetzt);
    const teil = (art: string) => teile.find(t => t.type === art)?.value ?? '';
    return `${teil('year')}-${teil('month')}-${teil('day')}`;
};

/**
 * TT.MM.JJJJ. Ein reines Datum (YYYY-MM-DD) wird ohne Zeitzone gelesen —
 * sonst rutschte es westlich von UTC auf den Vortag. Unlesbar → ''.
 */
export const datumDe = (wert: string | null | undefined): string => {
    if (!wert) return '';
    const d = NUR_DATUM.exec(wert.trim());
    if (d) return `${d[3]}.${d[2]}.${d[1]}`;
    const ms = Date.parse(wert);
    if (!Number.isFinite(ms)) return '';
    return new Date(ms).toLocaleDateString('de-DE', { timeZone: BERLIN, day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** TT.MM.JJJJ, HH:MM (Berlin). Unlesbar → ''. */
export const zeitpunktDe = (wert: string | null | undefined): string => {
    if (!wert) return '';
    const ms = Date.parse(wert);
    if (!Number.isFinite(ms)) return '';
    return new Date(ms).toLocaleString('de-DE', {
        timeZone: BERLIN, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

// ─── Standort und Suchpunkt ──────────────────────────────────────────────────

export interface Punkt {
    lat: number;
    lng: number;
}

const endlich = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

/** Hat die Bewerbung eine Nadel? Beide Koordinaten oder keine (Nr. 188 erzwingt das). */
export const hatStandort = (z: Pick<TalentpoolZeile, 'lat' | 'lng'>): boolean => endlich(z.lat) && endlich(z.lng);

/** Luftlinie zum Suchpunkt in km; ohne Standort null. */
export const entfernungKm = (z: Pick<TalentpoolZeile, 'lat' | 'lng'>, punkt: Punkt): number | null =>
    hatStandort(z) ? calculateDistanceKm(z.lat as number, z.lng as number, punkt.lat, punkt.lng) : null;

/**
 * Erreicht der Bewerber den Suchpunkt?
 *   true  — bundesweit, oder Luftlinie ≤ Umkreis
 *   false — Luftlinie größer als der Umkreis
 *   null  — unklar: kein Umkreis angegeben oder kein Standort
 * Bundesweit erreicht immer, auch ohne Nadel.
 */
export const erreicht = (z: Pick<TalentpoolZeile, 'lat' | 'lng' | 'umkreis_km' | 'bundesweit'>, punkt: Punkt): boolean | null => {
    if (z.bundesweit) return true;
    const km = entfernungKm(z, punkt);
    if (km === null || !endlich(z.umkreis_km) || z.umkreis_km <= 0) return null;
    return km <= z.umkreis_km;
};

/** „4,3 km“ unter 10 km, darüber ganze Kilometer („123 km“). */
export const entfernungText = (km: number): string =>
    km < 10
        ? `${km.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`
        : `${Math.round(km).toLocaleString('de-DE')} km`;

/** Umkreis für Liste und Detail. */
export const umkreisText = (z: Pick<TalentpoolZeile, 'umkreis_km' | 'bundesweit'>): string => {
    if (z.bundesweit) return 'bundesweit';
    if (endlich(z.umkreis_km) && z.umkreis_km > 0) return `bis ${z.umkreis_km.toLocaleString('de-DE')} km`;
    return 'Umkreis ohne Angabe';
};

/** Ort aus PLZ und Ortsangabe der Website; beides leer → ''. */
export const ortText = (z: Pick<TalentpoolZeile, 'plz_ort' | 'ort_naehe'>): string => {
    const plz = (z.plz_ort ?? '').trim();
    const ort = (z.ort_naehe ?? '').trim();
    if (plz && ort && ort.startsWith(plz)) return ort;
    return [plz, ort].filter(Boolean).join(' ');
};

// ─── Liste: Sortierung und Suchpunkt ─────────────────────────────────────────

/** Eine Zeile der Liste, mit Entfernung und Urteil zum Suchpunkt (ohne Punkt beides null). */
export interface TalentEintrag {
    zeile: TalentpoolZeile;
    km: number | null;
    erreicht: boolean | null;
}

const zeitMs = (iso: string): number => {
    const ms = Date.parse(iso);
    return Number.isFinite(ms) ? ms : 0;
};

/** Neueste zuerst; bei gleichem Eingang nach id, damit die Reihenfolge stabil bleibt. */
const neuesteZuerst = (a: TalentpoolZeile, b: TalentpoolZeile): number =>
    zeitMs(b.eingegangen_am) - zeitMs(a.eingegangen_am) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * Die Liste in Anzeigereihenfolge. Ohne Suchpunkt: neueste zuerst. Mit
 * Suchpunkt: nach Entfernung, wer keine Nadel hat, steht am Ende (dort
 * wieder neueste zuerst).
 */
export const eintraegeFuer = (zeilen: readonly TalentpoolZeile[], punkt: Punkt | null): TalentEintrag[] => {
    const eintraege = zeilen.map((zeile): TalentEintrag => ({
        zeile,
        km: punkt ? entfernungKm(zeile, punkt) : null,
        erreicht: punkt ? erreicht(zeile, punkt) : null,
    }));
    return eintraege.sort((a, b) => {
        if (punkt) {
            if (a.km !== null && b.km !== null && a.km !== b.km) return a.km - b.km;
            if (a.km === null && b.km !== null) return 1;
            if (a.km !== null && b.km === null) return -1;
        }
        return neuesteZuerst(a.zeile, b.zeile);
    });
};

/** Wie viele erreichen den Suchpunkt sicher (true)? */
export const anzahlErreicht = (eintraege: readonly TalentEintrag[]): number =>
    eintraege.filter(e => e.erreicht === true).length;

// ─── Filter ──────────────────────────────────────────────────────────────────

/** `offen` = noch nicht mit Zusage oder Absage abgeschlossen. */
export type StatusFilter = '' | 'offen' | TalentStatus;

export interface TalentFilter {
    /** '' = alle, sonst der Wert der Spalte `rolle`. */
    rolle: string;
    /** '' = alle, sonst ein Eintrag aus `arbeitsform`. */
    arbeitsform: string;
    status: StatusFilter;
    /** '' = alle, sonst genau diese Kampagne. */
    kampagne: string;
    /** Freitext über Name, PLZ/Ort und E-Mail; mehrere Wörter müssen alle passen. */
    text: string;
    /** Nur Bewerbungen, deren Löschfrist erreicht ist. */
    nurLoeschfaellig: boolean;
}

export const LEERER_FILTER: TalentFilter = {
    rolle: '', arbeitsform: '', status: '', kampagne: '', text: '', nurLoeschfaellig: false,
};

export const filterAktiv = (f: TalentFilter): boolean =>
    f.rolle !== '' || f.arbeitsform !== '' || f.status !== '' || f.kampagne !== ''
    || f.text.trim() !== '' || f.nurLoeschfaellig;

const klein = (s: string): string => s.toLocaleLowerCase('de-DE');

/** Ist die Löschfrist erreicht? `loeschen_ab` ≤ heute (beides YYYY-MM-DD). */
export const loeschFaellig = (z: Pick<TalentpoolZeile, 'loeschen_ab'>, heute: string): boolean =>
    typeof z.loeschen_ab === 'string' && z.loeschen_ab.length >= 10 && z.loeschen_ab.slice(0, 10) <= heute;

export const passtZumFilter = (z: TalentpoolZeile, f: TalentFilter, heute: string): boolean => {
    if (f.rolle && z.rolle !== f.rolle) return false;
    if (f.arbeitsform && !z.arbeitsform.includes(f.arbeitsform)) return false;
    if (f.status === 'offen' && istAbgeschlossen(z.status)) return false;
    if (f.status && f.status !== 'offen' && z.status !== f.status) return false;
    if (f.kampagne && (z.kampagne ?? '') !== f.kampagne) return false;
    if (f.nurLoeschfaellig && !loeschFaellig(z, heute)) return false;
    const woerter = klein(f.text).split(/\s+/).filter(Boolean);
    if (woerter.length > 0) {
        const heuhaufen = klein([z.name, z.plz_ort ?? '', z.ort_naehe ?? '', z.email ?? ''].join(' '));
        if (!woerter.every(w => heuhaufen.includes(w))) return false;
    }
    return true;
};

export const filtere = (zeilen: readonly TalentpoolZeile[], f: TalentFilter, heute: string): TalentpoolZeile[] =>
    zeilen.filter(z => passtZumFilter(z, f, heute));

/** Kampagnen für die Auswahl: jede einmal, alphabetisch. */
export const kampagnenAus = (zeilen: readonly TalentpoolZeile[]): string[] =>
    [...new Set(zeilen.map(z => (z.kampagne ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'de'));

/** Rollen für die Auswahl: die vier der Website, dazu jede fremde, die vorkommt. */
export const rollenAus = (zeilen: readonly TalentpoolZeile[]): string[] => {
    const fremde = [...new Set(zeilen.map(z => z.rolle).filter(r => !istTalentRolle(r)))].sort((a, b) => a.localeCompare(b, 'de'));
    return [...(Object.keys(ROLLE_LABEL) as TalentRolle[]), ...fremde];
};

// ─── Kopfzahlen ──────────────────────────────────────────────────────────────

export interface TalentZahlen {
    gesamt: number;
    neu: number;
    ohneStandort: number;
    loeschFaellig: number;
}

export const neuAnzahl = (zeilen: readonly TalentpoolZeile[]): number =>
    zeilen.filter(z => z.status === 'neu').length;

export const kopfZahlen = (zeilen: readonly TalentpoolZeile[], heute: string): TalentZahlen => ({
    gesamt: zeilen.length,
    neu: neuAnzahl(zeilen),
    ohneStandort: zeilen.filter(z => !hatStandort(z)).length,
    loeschFaellig: zeilen.filter(z => loeschFaellig(z, heute)).length,
});

// ─── Detail ──────────────────────────────────────────────────────────────────

/** Dateigröße für die Anhangliste: „850 KB“, „1,2 MB“; unbekannt (0) → ''. */
export const groesseText = (bytes: number): string => {
    if (!endlich(bytes) || bytes <= 0) return '';
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('de-DE')} KB`;
    return `${(bytes / (1024 * 1024)).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`;
};

/** Berufserfahrung: „1 Jahr“, „12 Jahre“; keine Angabe → ''. */
export const erfahrungText = (jahre: number | null): string =>
    endlich(jahre) ? (jahre === 1 ? '1 Jahr' : `${jahre.toLocaleString('de-DE')} Jahre`) : '';

/** Wie viele angekündigte Anhänge noch nicht in der Ablage liegen. */
export const dateienFehlen = (z: Pick<TalentpoolZeile, 'dateien' | 'dateien_erwartet'>): number =>
    Math.max(0, z.dateien_erwartet - z.dateien.length);

/**
 * Löschfrist in einem Satz. Mit Talentpool-Einwilligung zwei Jahre ab
 * Eingang, sonst sechs Monate ab Zusage/Absage (Trigger Nr. 189).
 */
export const loeschfristText = (z: Pick<TalentpoolZeile, 'loeschen_ab' | 'einwilligung_talentpool'>, heute: string): string => {
    if (!z.loeschen_ab) {
        return z.einwilligung_talentpool
            ? 'Löschfrist noch nicht berechnet.'
            : 'Löschfrist beginnt mit Zusage oder Absage (dann sechs Monate).';
    }
    return loeschFaellig(z, heute)
        ? `Löschfrist erreicht (seit ${datumDe(z.loeschen_ab)}).`
        : `Löschen ab ${datumDe(z.loeschen_ab)}.`;
};

/** `tel:`-Link aus der Telefonangabe; zu wenig Ziffern → null. */
export const telHref = (telefon: string | null): string | null => {
    const nummer = (telefon ?? '').replace(/[^\d+]/g, '');
    return nummer.replace(/\+/g, '').length >= 3 ? `tel:${nummer}` : null;
};

const MAIL_FORM = /^[^\s@<>"',;:()[\]\\?&#]+@[^\s@<>"',;:()[\]\\?&#]+\.[^\s@<>"',;:()[\]\\?&#]+$/;

/**
 * `mailto:`-Link; was nicht wie eine Adresse aussieht → null (dann nur Text).
 * `?`, `&` und `#` sind ausgeschlossen: aus einem Formularfeld soll kein
 * Betreff oder Text in die Mail wandern.
 */
export const mailHref = (email: string | null): string | null => {
    const adresse = (email ?? '').trim();
    return MAIL_FORM.test(adresse) ? `mailto:${adresse}` : null;
};

/** Titel der Zusatzangaben wie im Admin der Website (BEWERBUNG_TITEL dort). */
const ANGABE_TITEL: Readonly<Record<string, string>> = {
    umkreis: 'Umkreis',
    tage: 'Tage pro Woche',
    verfuegbar: 'Verfügbar',
    ausbildung: 'Ausbildung',
};
/** Steht schon als eigene Spalte da (Ring, Kampagnenfilter) — im Detail nicht doppelt. */
const ANGABE_AUSGEBLENDET: ReadonlySet<string> = new Set(['umkreis_km', 'kampagne']);

const angabeWert = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number') return Number.isFinite(v) ? v.toLocaleString('de-DE') : '';
    if (typeof v === 'boolean') return v ? 'ja' : 'nein';
    if (Array.isArray(v)) return v.map(angabeWert).filter(Boolean).join(', ');
    try {
        return JSON.stringify(v) ?? '';
    } catch {
        return '';
    }
};

export interface LesbareAngabe {
    schluessel: string;
    titel: string;
    wert: string;
}

/**
 * Zusatzangaben der Website als Zeilen: die bekannten zuerst mit Titel
 * (Umkreis, Tage pro Woche, Verfügbar, Ausbildung), alles andere als
 * „schluessel: wert“ in der Reihenfolge der Lieferung. Leere Werte fallen weg.
 */
export const lesbareAngaben = (angaben: Readonly<Record<string, unknown>>): LesbareAngabe[] => {
    const bekannt: LesbareAngabe[] = [];
    const rest: LesbareAngabe[] = [];
    for (const schluessel of Object.keys(ANGABE_TITEL)) {
        if (!Object.prototype.hasOwnProperty.call(angaben, schluessel)) continue;
        const wert = angabeWert(angaben[schluessel]);
        if (wert) bekannt.push({ schluessel, titel: ANGABE_TITEL[schluessel], wert });
    }
    for (const [schluessel, roh] of Object.entries(angaben)) {
        if (ANGABE_AUSGEBLENDET.has(schluessel) || Object.prototype.hasOwnProperty.call(ANGABE_TITEL, schluessel)) continue;
        const wert = angabeWert(roh);
        if (wert) rest.push({ schluessel, titel: schluessel, wert });
    }
    return [...bekannt, ...rest];
};

// ─── Bearbeiten ──────────────────────────────────────────────────────────────

/** Notiz so, wie die RPC sie speichert: getrimmt und auf die Grenze gekürzt. */
export const notizBereinigt = (notiz: string): string => notiz.trim().slice(0, TALENT_GRENZEN.notiz);

/** Hat sich gegenüber der gespeicherten Zeile etwas geändert? */
export const hatAenderung = (z: Pick<TalentpoolZeile, 'status' | 'notiz'>, status: TalentStatus, notiz: string): boolean =>
    status !== z.status || notizBereinigt(notiz) !== (z.notiz ?? '').trim();

/** Zeile ersetzen (gleiche id) — Antwort der RPC sofort in die Liste übernehmen. */
export const ersetzeZeile = (zeilen: readonly TalentpoolZeile[], neu: TalentpoolZeile): TalentpoolZeile[] =>
    zeilen.some(z => z.id === neu.id) ? zeilen.map(z => (z.id === neu.id ? neu : z)) : [neu, ...zeilen];

export const ohneZeile = (zeilen: readonly TalentpoolZeile[], id: string): TalentpoolZeile[] =>
    zeilen.filter(z => z.id !== id);
